import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  computed,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { UmssIconComponent } from '../icon/icon.component';

export interface FileDropzoneItem {
  file: File;
  id: string;
}

@Component({
  selector: 'umss-file-dropzone',
  standalone: true,
  imports: [UmssIconComponent],
  templateUrl: './file-dropzone.component.html',
  styleUrl: './file-dropzone.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.display]': "'contents'"
  }
})
export class UmssFileDropzoneComponent {
  /** Accepted file types (e.g. ".pdf,.doc,image/*"). Passed to the hidden input. */
  accept = input<string>('');

  /** Allow selecting multiple files at once */
  multiple = input<boolean>(true);

  /** Max file size in bytes. Files exceeding this are silently rejected. */
  maxBytes = input<number | undefined>(undefined);

  /** Text shown on the clickable/droppable trigger area */
  attachText = input<string>('Attach Files');

  /** Icon name for the trigger area */
  iconName = input<string>('phosphorPaperclip');

  /** Icon color for the trigger area */
  iconColor = input<string>('#4361EE');

  /** Whether the dropzone is disabled */
  disabled = input<boolean>(false);

  /** Emitted whenever the file list changes */
  filesChange = output<File[]>();

  private readonly _items = signal<FileDropzoneItem[]>([]);
  readonly items = this._items.asReadonly();
  readonly hasFiles = computed(() => this._items().length > 0);

  private readonly _isDragOver = signal(false);
  readonly isDragOver = this._isDragOver.asReadonly();

  private readonly _fileInput =
    viewChild<ElementRef<HTMLInputElement>>('fileInput');

  // ── Interaction handlers ──────────────────────────────────────────────────

  onTriggerClick(): void {
    if (this.disabled()) return;
    this._fileInput()?.nativeElement.click();
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onTriggerClick();
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this._addFiles(Array.from(input.files));
      // Reset input so re-selecting the same file fires the change event again
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this._isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this._isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    event.stopPropagation();
    this._isDragOver.set(false);
    const files = event.dataTransfer?.files;
    if (files) {
      this._addFiles(Array.from(files));
    }
  }

  removeFile(id: string): void {
    this._items.update((items) => items.filter((item) => item.id !== id));
    this._emitChange();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private _addFiles(files: File[]): void {
    const maxBytes = this.maxBytes();
    const accepted = files.filter(
      (f) => maxBytes === undefined || f.size <= maxBytes
    );

    const newItems: FileDropzoneItem[] = accepted.map((file) => ({
      file,
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`
    }));

    if (this.multiple()) {
      this._items.update((prev) => [...prev, ...newItems]);
    } else {
      this._items.set(newItems.slice(0, 1));
    }

    this._emitChange();
  }

  private _emitChange(): void {
    this.filesChange.emit(this._items().map((i) => i.file));
  }

  getFileIcon(file: File): string {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const mime = file.type.toLowerCase();

    if (mime.startsWith('image/')) {
      if (ext === 'jpg' || ext === 'jpeg') return 'phosphorFileJpg';
      if (ext === 'png') return 'phosphorFilePng';
      if (ext === 'svg') return 'phosphorFileSvg';
      return 'phosphorFileImage';
    }
    if (mime.startsWith('audio/')) return 'phosphorFileAudio';
    if (mime.startsWith('video/')) return 'phosphorFileVideo';

    switch (ext) {
      case 'pdf':
        return 'phosphorFilePdf';
      case 'doc':
      case 'docx':
        return 'phosphorFileDoc';
      case 'xls':
      case 'xlsx':
        return 'phosphorFileXls';
      case 'ppt':
      case 'pptx':
        return 'phosphorFilePpt';
      case 'csv':
        return 'phosphorFileCsv';
      case 'txt':
        return 'phosphorFileTxt';
      case 'md':
        return 'phosphorFileMd';
      case 'zip':
      case 'tar':
      case 'gz':
      case 'rar':
      case '7z':
        return 'phosphorFileArchive';
      case 'html':
      case 'htm':
        return 'phosphorFileHtml';
      case 'css':
        return 'phosphorFileCss';
      case 'js':
        return 'phosphorFileJs';
      case 'jsx':
        return 'phosphorFileJsx';
      case 'ts':
        return 'phosphorFileTs';
      case 'tsx':
        return 'phosphorFileTsx';
      case 'vue':
        return 'phosphorFileVue';
      case 'py':
        return 'phosphorFilePy';
      case 'c':
        return 'phosphorFileC';
      case 'cpp':
      case 'cc':
        return 'phosphorFileCpp';
      case 'cs':
        return 'phosphorFileCSharp';
      case 'sql':
        return 'phosphorFileSql';
      default:
        return 'phosphorFile';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    return `${value % 1 === 0 ? value : value.toFixed(1)} ${units[i]}`;
  }
}
